import {ChangeEvent, ComponentType, ReactElement, useState} from 'react'
import {I18nContextValues, Trans, withNamespaces} from 'react-i18next'
import {flowRight as compose} from 'lodash'
import {withStyles, StyledComponentProps, FormControl, InputLabel, Select, MenuItem, Grid, FormHelperText} from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import {performanceFeeOptions} from '../../../../../../../common/utils/uioptions'
import {formatPercent} from '../../../../../../../common/utils'
import NotificationBar from '../../../../../../Common/NotificationBar'
import messages from '../../../../../../../assets/messages'
import {AlertDialog} from '../../../../../../Common/Dialog'
import {useUpdateProvider} from '../../hooks/useUpdateProvider'
import {styles} from './styles'

const PERFORMANCE_FEE_FALLBACK = ''

type PerformanceFeeInputBaseProps = {
  accountId: number
  value: number | undefined
  disabled: boolean
  hasActiveSubscriptions: boolean
  onUpdated: () => Promise<void>
}

type PerformanceFeeInputProps = StyledComponentProps & I18nContextValues & PerformanceFeeInputBaseProps

const Component = (props: PerformanceFeeInputProps): ReactElement => {
  const {t, lng, classes = {}, accountId, value, disabled, hasActiveSubscriptions, onUpdated} = props

  const [performanceFeeDraft, setPerformanceFeeDraft] = useState<number | null>(null)

  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleFeeChange = (e: ChangeEvent<{ name?: string; value: unknown }>): void => {
    setPerformanceFeeDraft(Number(e.target.value))
  }

  const handleUpdateSuccess = async (): Promise<void> => {
    await onUpdated()
    setPerformanceFeeDraft(null)
    setErrorMessage(null)
    setSuccessMessage(t(messages.brokereePerformanceFeeUpdateSuccess.i18nKey, messages.brokereePerformanceFeeUpdateSuccess.defaults))
  }

  const handleUpdateError = (): void => {
    // TODO: update error message
    setErrorMessage(t(messages.somethingWrongError.i18nKey, messages.somethingWrongError.defaults))
    setSuccessMessage(null)
  }

  const {updateProvider, loading} = useUpdateProvider(accountId, {onError: handleUpdateError, onSuccess: handleUpdateSuccess})

  const handleDialogAgree = async (): Promise<void> => {
    if (performanceFeeDraft == null) return
    await updateProvider({performanceFee: performanceFeeDraft})
  }

  const handleDialogClose = (): void => {
    setPerformanceFeeDraft(null)
  }

  const handleNotificationClose = (): void => {
    setSuccessMessage(null)
  }

  return (
    <>
      <Grid item sm={10} xs={12}>
        <FormControl fullWidth disabled={disabled}>
          <InputLabel htmlFor="performanceFee">
            <Trans {...messages.performanceFee} />
          </InputLabel>
          <Select
            value={value ?? PERFORMANCE_FEE_FALLBACK}
            onChange={handleFeeChange}
          >
            <MenuItem disabled value={PERFORMANCE_FEE_FALLBACK}><Trans {...messages.performanceFee} /></MenuItem>
            {performanceFeeOptions.options.map(fee =>
              <MenuItem key={fee} value={fee} disabled={fee === value}>{formatPercent(lng, fee)}</MenuItem>
            )}
          </Select>
          {hasActiveSubscriptions ? (
            <FormHelperText>
              <Trans {...messages.brokereeHasActiveSubscriptionsNotification} />
            </FormHelperText>
          ) : null}
        </FormControl>
      </Grid>

      {successMessage != null ?
        (<Grid item sm={10} xs={12}>
          <NotificationBar status="success" onClose={handleNotificationClose}>
            {successMessage}
          </NotificationBar>
        </Grid>)
        : null}

      <AlertDialog
        open={performanceFeeDraft != null}
        onClose={handleDialogClose}
        onAgree={handleDialogAgree}
        onDisagree={handleDialogClose}
        disagreeText={t(messages.cancel.i18nKey, messages.cancel.defaults)}
        agreeText={t(messages.confirm.i18nKey, messages.confirm.defaults)}
        title = {<Typography variant='body2' className={classes.dialogTitle}>
          <Trans {...messages.brokereeChangePerformanceFeePopupTitle} />
        </Typography>}
        buttonLoading={loading}
        buttonStatus={errorMessage != null ? 'failure' : null}
      >
        <>
          <Typography variant="body1">
            <Trans {...messages.changeFeePopupText1} values={{fee: value}} components={[<span className={classes.highlightText}>oldFee</span>]} />
          </Typography>
          <Typography variant="body1">
            <Trans {...messages.changeFeePopupText2} values={{feeChange: performanceFeeDraft}} components={[<span className={classes.highlightText}>newFee</span>]}/>
          </Typography>
          {errorMessage != null ? <NotificationBar status="error">{errorMessage}</NotificationBar> : null}
        </>
      </AlertDialog>
    </>


  )
}

export const PerformanceFeeInput: ComponentType<PerformanceFeeInputBaseProps> = compose(
  withStyles(styles),
  withNamespaces()
)(Component)
